import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { FrameBlockConfig, UnitValue } from '@front-platform/content-builder';
import {
  frameBlockResolveConfig,
  normalizeUnitValue,
  serializeSpacingBoxSide,
  serializeUnitValue,
  type UnitValueChoice,
} from '@front-platform/content-builder';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { BlockListComponent } from '@core/components/block-list/block-list.component';
import { NgStyle } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, Renderer2 } from '@angular/core';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import {
  omitInlineStylesOverriddenByCustomCss,
  scopeFrameCustomCss,
} from './frame-block-custom-css.util';

export type FrameLayout = 'vertical' | 'horizontal' | 'grid';
export type FrameAlignment =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type FrameSpacingBox = {
  top?: UnitValue | number;
  bottom?: UnitValue | number;
  left?: UnitValue | number;
  right?: UnitValue | number;
};

export interface FrameBlockRuntimeConfig {
  body?: unknown;
}

export type FrameBlockConfigInput = Partial<
  Omit<FrameBlockConfig, keyof FrameBlockRuntimeConfig> & FrameBlockRuntimeConfig
>;
export type FrameBlockResolvedConfig = Omit<FrameBlockConfig, keyof FrameBlockRuntimeConfig> &
  Partial<FrameBlockRuntimeConfig>;
export interface FrameBlockContract extends ConfigComponentContract<
  FrameBlockConfigInput,
  FrameBlockResolvedConfig
> {}

const FRAME_LENGTH_UNITS: readonly UnitValueChoice[] = [
  { value: 'px', label: 'px' },
  { value: '%', label: '%' },
  { value: 'rem', label: 'rem' },
  { value: 'vw', label: 'vw' },
  { value: 'vh', label: 'vh' },
];

const FRAME_FLEX_GAP_UNITS: readonly UnitValueChoice[] = [
  { value: 'space-between', label: 'space-between', inputDisabled: true },
  { value: 'space-around', label: 'space-around', inputDisabled: true },
  { value: 'space-evenly', label: 'space-evenly', inputDisabled: true },
  { value: 'auto', label: 'auto', inputDisabled: true },
  ...FRAME_LENGTH_UNITS,
];

const FRAME_SIZE_UNITS: readonly UnitValueChoice[] = [
  { value: 'auto', label: 'auto', inputDisabled: true },
  ...FRAME_LENGTH_UNITS,
];

const FRAME_MARGIN_UNITS: readonly UnitValueChoice[] = [
  { value: 'auto', label: 'auto', inputDisabled: true },
  ...FRAME_LENGTH_UNITS,
];

const ALIGNMENT_MAP: Record<
  FrameAlignment,
  {
    vertical: 'flex-start' | 'center' | 'flex-end';
    horizontal: 'flex-start' | 'center' | 'flex-end';
  }
> = {
  'top-left': { vertical: 'flex-start', horizontal: 'flex-start' },
  'top-center': { vertical: 'flex-start', horizontal: 'center' },
  'top-right': { vertical: 'flex-start', horizontal: 'flex-end' },
  'center-left': { vertical: 'center', horizontal: 'flex-start' },
  center: { vertical: 'center', horizontal: 'center' },
  'center-right': { vertical: 'center', horizontal: 'flex-end' },
  'bottom-left': { vertical: 'flex-end', horizontal: 'flex-start' },
  'bottom-center': { vertical: 'flex-end', horizontal: 'center' },
  'bottom-right': { vertical: 'flex-end', horizontal: 'flex-end' },
};

@Component({
  selector: 'app-frame-block',
  standalone: true,
  imports: [NgStyle, BlockListComponent],
  templateUrl: './frame-block.component.html',
  styleUrl: './frame-block.component.scss',
})
export class FrameBlockComponent implements FrameBlockContract {
  private static nextCssScopeId = 0;

  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private customCssStyleEl: HTMLStyleElement | null = null;

  readonly cssScopeClass = `cb-frame-${++FrameBlockComponent.nextCssScopeId}`;
  readonly config = input<FrameBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<FrameBlockConfig, FrameBlockRuntimeConfig>(
    frameBlockResolveConfig,
    () => ({ config: this.config() }),
  );

  readonly body = computed(() => (this.resolvedConfig()['body'] as unknown[] | undefined) ?? []);
  readonly scopedCustomCss = computed(() =>
    scopeFrameCustomCss(this.resolvedConfig()['custom_css'], `.${this.cssScopeClass}`),
  );

  constructor() {
    effect(() => this.syncCustomCssStyle(this.scopedCustomCss()));
    this.destroyRef.onDestroy(() => this.removeCustomCssStyle());
  }

  readonly frameStyles = computed(() => {
    const config = this.resolvedConfig();
    const layout = (config['layout'] as FrameLayout | undefined) ?? 'vertical';
    const alignment = (config['alignment'] as FrameAlignment | undefined) ?? 'top-left';
    const gap = this.resolveGap(config['gap']);
    const distributeGap = this.resolveDistributeGap(gap.unit);
    const padding = (config['padding'] as FrameSpacingBox | undefined) ?? {};
    const margin = (config['margin'] as FrameSpacingBox | undefined) ?? {};
    const backgroundColor = config['background_color'] as unknown as
      IContentBuilderFieldColor | string | undefined;
    const backgroundImage = config['background_image'] as unknown as
      IContentBuilderFieldImage | string | undefined;
    const backgroundRepeat =
      (config['background_repeat'] as unknown as string | undefined) ?? 'no-repeat';
    const align = ALIGNMENT_MAP[alignment] ?? ALIGNMENT_MAP['top-left'];
    const showStroke = Boolean(config['show_stroke']);
    const showDropShadow = Boolean(config['show_drop_shadow']);

    const styles: Record<string, string | null> = {
      display: layout === 'grid' ? 'grid' : 'flex',
      width: this.resolveSize(config['width']),
      height: this.resolveSize(config['height']),
      'padding-top': serializeSpacingBoxSide(padding.top, FRAME_LENGTH_UNITS),
      'padding-bottom': serializeSpacingBoxSide(padding.bottom, FRAME_LENGTH_UNITS),
      'padding-left': serializeSpacingBoxSide(padding.left, FRAME_LENGTH_UNITS),
      'padding-right': serializeSpacingBoxSide(padding.right, FRAME_LENGTH_UNITS),
      'margin-top': serializeSpacingBoxSide(margin.top, FRAME_MARGIN_UNITS),
      'margin-bottom': serializeSpacingBoxSide(margin.bottom, FRAME_MARGIN_UNITS),
      'margin-left': serializeSpacingBoxSide(margin.left, FRAME_MARGIN_UNITS),
      'margin-right': serializeSpacingBoxSide(margin.right, FRAME_MARGIN_UNITS),
      'background-color': fromContentBuilderColor(backgroundColor, 'transparent'),
      'background-image': this.backgroundImageUrl(backgroundImage),
      'background-repeat': backgroundImage ? backgroundRepeat : null,
      'background-position': backgroundImage ? 'center' : null,
      'background-size': backgroundImage
        ? backgroundRepeat === 'no-repeat'
          ? 'cover'
          : 'auto'
        : null,
      'border-radius': serializeUnitValue(
        normalizeUnitValue(config['border_radius'], { unit: 'px', value: 0 }),
        FRAME_LENGTH_UNITS,
      ),
      border: showStroke
        ? `${this.toPx(config['stroke_width'] as number | undefined)} solid ${fromContentBuilderColor(
            config['stroke_color'] as unknown as IContentBuilderFieldColor | string | undefined,
            '#000000',
          )}`
        : null,
      'box-shadow': showDropShadow
        ? [
            this.toPx(config['drop_shadow_offset_x'] as number | undefined),
            this.toPx(config['drop_shadow_offset_y'] as number | undefined),
            this.toPx(config['drop_shadow_blur'] as number | undefined),
            this.toPx(config['drop_shadow_spread'] as number | undefined),
            fromContentBuilderColor(
              config['drop_shadow_color'] as unknown as
                IContentBuilderFieldColor | string | undefined,
              'rgba(0, 0, 0, 0.25)',
            ),
          ].join(' ')
        : null,
    };

    if (layout === 'grid') {
      const columns = Number(config['columns'] ?? 2) || 2;
      styles['grid-template-columns'] = `repeat(${columns}, 1fr)`;
      styles['column-gap'] = serializeUnitValue(
        this.resolveGap(config['column_gap']),
        FRAME_LENGTH_UNITS,
      );
      styles['row-gap'] = serializeUnitValue(
        this.resolveGap(config['row_gap']),
        FRAME_LENGTH_UNITS,
      );
      return this.withCustomCss(styles, config['custom_css']);
    }

    styles['flex-direction'] = layout === 'horizontal' ? 'row' : 'column';
    styles['flex-wrap'] = 'nowrap';

    if (distributeGap) {
      styles['justify-content'] = distributeGap;
      styles['align-items'] = layout === 'horizontal' ? align.vertical : align.horizontal;
      styles['gap'] = '0';
      return this.withCustomCss(styles, config['custom_css']);
    }

    if (layout === 'horizontal') {
      styles['justify-content'] = align.horizontal;
      styles['align-items'] = align.vertical;
    } else {
      styles['justify-content'] = align.vertical;
      styles['align-items'] = align.horizontal;
    }
    styles['gap'] = serializeUnitValue(gap, FRAME_FLEX_GAP_UNITS);

    return this.withCustomCss(styles, config['custom_css']);
  });

  /** Drop inline props that the scoped stylesheet will set (inline would win). */
  private withCustomCss(
    styles: Record<string, string | null>,
    customCss: unknown,
  ): Record<string, string | null> {
    return omitInlineStylesOverriddenByCustomCss(styles, customCss);
  }

  private syncCustomCssStyle(css: string): void {
    if (!css) {
      this.removeCustomCssStyle();
      return;
    }
    if (!this.customCssStyleEl) {
      this.customCssStyleEl = this.renderer.createElement('style') as HTMLStyleElement;
      this.renderer.setAttribute(this.customCssStyleEl, 'data-cb-frame-css', this.cssScopeClass);
      this.renderer.appendChild(document.head, this.customCssStyleEl);
    }
    this.renderer.setProperty(this.customCssStyleEl, 'textContent', css);
  }

  private removeCustomCssStyle(): void {
    if (!this.customCssStyleEl) {
      return;
    }
    this.renderer.removeChild(document.head, this.customCssStyleEl);
    this.customCssStyleEl = null;
  }

  private resolveGap(value: unknown): UnitValue {
    return normalizeUnitValue(value, { unit: 'px', value: 0 });
  }

  /** Fixed gap distance vs distribute leftover space along the main axis. */
  private resolveDistributeGap(
    unit: string,
  ): 'space-between' | 'space-around' | 'space-evenly' | null {
    if (unit === 'auto') {
      return 'space-between';
    }
    if (unit === 'space-between' || unit === 'space-around' || unit === 'space-evenly') {
      return unit;
    }
    return null;
  }

  /** Auto → fit-content so the frame hugs its children. */
  private resolveSize(value: unknown): string {
    const size = normalizeUnitValue(value, { unit: 'auto', value: 0 });
    if (size.unit === 'auto') {
      return 'fit-content';
    }
    return serializeUnitValue(size, FRAME_SIZE_UNITS);
  }

  private toPx(value: number | string | undefined | null): string {
    if (value === undefined || value === null || value === '') {
      return '0';
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return `${numeric}px`;
    }
    return String(value);
  }

  private backgroundImageUrl(
    image: IContentBuilderFieldImage | string | undefined | null,
  ): string | null {
    if (!image) {
      return null;
    }
    if (typeof image === 'string') {
      return image ? `url(${image})` : null;
    }
    return image.url ? `url(${image.url})` : null;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(FrameBlockComponent, { keyAliases: {} });
